<script setup lang="ts">
import { getMaterialPublicId } from '~/shared/lib/getMaterialLink';
import type {
  INewsDataClientDTO,
  INewsItemDataClientDTO,
} from '~/shared/api/client/news/news.dto';
import { resizeCropSlot, resizeImage } from '~/shared/lib/resizeImage';
import { CROP_RATIO_CHRONICLE } from '~/shared/lib/crop-ratio.const';
import { NEWS_ITEM_SMALL_IMAGE_WIDTH } from './lib/const';

type INewsDataClientDTOWithExpert = Omit<INewsDataClientDTO, 'news'> & {
  news: INewsItemDataClientDTO | null;
};

const props = defineProps<INewsDataClientDTOWithExpert>();

const isExpert = computed(
  () => props.expert !== null && props.expert !== undefined,
);

const isRedirect = computed(() =>
  Boolean(props.news?.redirect && props.news.redirect.length > 0),
);

const link = computed(() => {
  if (isRedirect.value && props.news?.redirect) return props.news.redirect;
  if (isExpert.value) return `/expert/${getMaterialPublicId(props.expert!)}`;
  return `/${props.news?.type}/${props.news?.id}`;
});

const categoryLink = computed(() =>
  isExpert.value ? '/expert' : `/category/${props.news?.category?.id}`,
);
const categoryName = computed(() =>
  isExpert.value ? 'Мнения экспертов' : props.news?.category?.name,
);
const partnerCategory = computed(
  () => props.expert?.category ?? props.news?.category ?? null,
);

const imageOne = computed(
  () =>
    props.image_one?.file_url || props.news?.image?.photo.file_url || undefined,
);

const imageOneAttrs = computed(() =>
  resizeCropSlot(imageOne.value, CROP_RATIO_CHRONICLE.image_right_single, 0, {
    width: NEWS_ITEM_SMALL_IMAGE_WIDTH,
  }),
);

const isGalleryFree = computed(
  () => !!props.news?.is_gallery && !props.news?.is_paid,
);
const isGalleryPaid = computed(
  () => !!props.news?.is_gallery && !!props.news?.is_paid,
);
const isGalleryPaidLogo = computed(
  () =>
    !!props.news?.is_gallery &&
    !!props.news?.is_paid &&
    !!props.news?.logo?.photo.file_url,
);
</script>

<template>
  <div
    :class="[
      $style.item,
      {
        [$style.item_galleryFree!]: isGalleryFree,
        [$style.item_galleryPaid!]: isGalleryPaid,
        [$style.item_galleryPaidLogo!]: isGalleryPaidLogo,
        [$style.item_dark!]: props.news?.type === 'new_object',
      },
    ]"
  >
    <div :class="$style.item__info">
      <div :class="$style.item__categoryRow">
        <NuxtLink :class="$style.item__category" :to="categoryLink">
          {{ categoryName }}
          <Icon
            v-if="isGalleryFree || isGalleryPaidLogo"
            name="my-icon:i-camera"
            size="20"
          />
        </NuxtLink>

        <PartnerBadge :category="partnerCategory" />
      </div>

      <NuxtLink
        :class="$style.item__title"
        :to="link"
        :target="!isExpert && isRedirect ? '_blank' : undefined"
      >
        {{ title }}
      </NuxtLink>
    </div>
    <NuxtLink
      v-if="props.image_one"
      :class="$style.item__imageWrapper"
      :to="link"
      :target="!isExpert && isRedirect ? '_blank' : undefined"
    >
      <img
        :src="imageOneAttrs.src"
        :srcset="imageOneAttrs.srcset || undefined"
        :alt="title"
        :class="$style.item__imageWrapper"
      />
      <Icon
        v-if="isGalleryPaid && !isGalleryPaidLogo"
        name="my-icon:i-camera-red-square"
        size="32"
        :class="$style.item__imageIcon"
      />
      <div
        v-if="isGalleryPaid && isGalleryPaidLogo"
        :style="{
          backgroundImage: `url(${resizeImage(props.news?.logo?.photo.file_url, { width: 120 })})`,
        }"
        :class="$style.item__imagePartner"
      />
    </NuxtLink>
  </div>
</template>

<style lang="scss" module>
.item {
  display: flex;
  flex-flow: row nowrap;
  gap: var(--space-1x);
  border-bottom: 1px solid var(--color-gray-line);
  position: relative;
  justify-content: space-between;
  height: 100%;

  &_galleryFree {
    background: linear-gradient(270deg, #ffffff 71.67%, #f1f1f1 100%);
  }

  &_galleryPaid {
    border-left: 4px solid var(--color-red-1);
    background: linear-gradient(
      90deg,
      rgba(185, 6, 24, 0.2) 0%,
      rgba(220, 188, 62, 0) 100%
    );

    .item {
      &__category {
        color: var(--color-red-1);
      }
    }
  }

  &_galleryPaidLogo {
    border-left: none;
    background: none;

    .item {
      &__category {
        color: var(--color-red-1);
      }
    }
  }

  &:last-of-type {
    border-bottom: none;
  }

  &_dark {
    background-color: var(--color-black-1);
  }

  &__info {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-1x);
  }

  &__categoryRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-1x);
  }

  &__category {
    display: flex;
    @include text_caption2_caps;
    color: var(--color-gray-text);
    text-decoration: none;
    gap: var(--space-1x);
    align-items: center;
    z-index: 1;
  }

  &_dark &__category {
    color: var(--color-white);
  }

  &__title {
    display: block;
    @include text_body2;
    color: var(--color-black-1);
    text-decoration: none;
  }

  &_dark &__title {
    color: var(--color-white);
  }

  &__image {
    width: 80px;
    height: auto;

    &Wrapper {
      position: relative;
      flex-shrink: 0;
      width: 80px;
      height: 80px;
      object-fit: cover;
    }

    &Icon {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    &Partner {
      position: absolute;
      left: -20px;
      top: -20px;
      width: 38px;
      height: 38px;
      background-color: var(--color-red-1);
      background-size: 24px;
      background-position: center;
      background-repeat: no-repeat;
    }
  }

  &__icon {
    position: absolute;
    top: var(--space-2-5x);
    right: var(--space-2-5x);
    path {
      fill: var(--color-yellow-1);
    }
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 479px) {
}
</style>
